import { toFirstUpperCase } from 'infrastructure/functions';

import {
  factorsBlurb,
  medicationBlurb,
  nursingInterventionBlurb,
  Q1Blurb,
  Q2Blurb,
  Q3Blurb,
  Q4Blurb,
  symptomsBlurb,
  verbalizedBlurb,
} from './common';

type TData = {
  [k: string]: Record<string, any>;
};

export const generateSpO2Message = <T extends TData>(data: T) => {
  const { Q1, Q2, Q3, Q4, Q5, Q6, Q7, Q8, Q9, Q10, Q11 } = data;
  let message = '';

  const relationship = Q1?.relationship
    ? toFirstUpperCase(Q1.relationship)
    : '';
  const speakWith = Q1.speakWithPatient
    ? 'Patient'
    : `${Q1.patient ?? ''}${relationship}`;
  const thirdPersonText = Q1.speakWithPatient ? '' : ' states that patient';
  const speakerStatement = speakWith + thirdPersonText;

  // Q1
  if (Q1) {
    message += Q1Blurb({
      reading: Q1.reading,
      dateOfReading: Q1.dateOfReading,
      patient: Q1.patient,
      speakWithOther: Q1.speakWithOther,
      relationship: Q1.relationship,
      readingName: 'oxygen',
    });
    message += '\n';
  }

  // Q2
  if (Q2.AOC) {
    message += Q2Blurb(speakerStatement, Q2.AOC);
    message += '\n';
  }

  // Q3
  if (Q3.last || Q3.next) {
    message += Q3Blurb(speakerStatement, Q3.next, Q3.last);
    message += '\n';
  }

  // Q4
  message += Q4Blurb(speakerStatement, {
    isOneMoreReading: Q4.isOneMoreReading,
    bounds: Q4.bounds,
    repeatReadingWas: Q4.repeatReadingWas,
  });
  message += '\n';

  // Q5
  message += symptomsBlurb({
    speakerStatement,
    emergencyModalAnswer: Q5.emergencyModalAnswer,
    confirmedSymptoms: Q5.confirmedSymptoms,
    deniedSymptoms: Q5.deniedSymptoms,
    confirmedEmergentSymptoms: Q5.confirmedEmergentSymptoms,
    symptomsOnset: Q5.symptomsOnset,
    isAllSymptomsDenied: Q5.isAllSymptomsDenied,
    speakWith,
    speakWithPatient: Q1.speakWithPatient,
  });
  message += '\n';

  // Q6
  if (Q6.isTakeMedications !== null) {
    message += medicationBlurb({
      isTakeMedications: Q6.isTakeMedications,
      speakerStatement,
      speakWithPatient: Q1?.speakWithPatient,
      type: 'routine',
      medications: Q6.medications,
    });
    message += '\n';
  }
  // Q7
  if (Q7.isTakePrnMedications !== null) {
    message += medicationBlurb({
      isTakeMedications: Q7.isTakePrnMedications,
      speakerStatement,
      speakWithPatient: Q1?.speakWithPatient,
      type: 'any PRN',
      medications: Q6.prnMedications,
    });
    message += '\n';
  }

  // Q8
  if (Q8.wearO2Home !== null || Q8.homeO2Setting !== null) {
    if (typeof Q8.wearO2Home === 'boolean') {
      message += `${speakerStatement} ${
        Q8.wearO2Home ? 'does' : 'does not'
      } wear oxygen at home. `;
    }
    if (Q8.homeO2Setting) {
      message += `The current setting is ${Q8.homeO2Setting} L/min`;
      if (Q8.homeO2SettingValue !== null) {
        message += ` ${Q8.homeO2SettingValue}`;
      }
      message += '. ';
    }
    message += '\n';
  }

  // Q9
  if (Q9.confirmedFactors?.length > 0 || Q9.deniedFactors?.length > 0) {
    message += factorsBlurb({
      speakerStatement,
      confirmedFactors: Q9.confirmedFactors,
      deniedFactors: Q9.deniedFactors,
      readingName: 'SpO2',
    });
    message += '\n';
  }

  // Q10
  message += nursingInterventionBlurb({
    escalationToClinic: Q10.escalationToClinic,
    notEscalated: Q10.notEscalated,
    interventionDetails: Q10.interventionDetails,
    emergencyRoom: Q10.emergencyRoom,
    escalation: Q10.escalation,
    nursingEducation: Q10.nursingEducation,
  });
  message += '\n';

  // Q11
  if (Q11.verbalized !== null) {
    message += verbalizedBlurb(speakerStatement, Q11.verbalized);
    message += '\n';
  }

  return message.trim();
};
